import { format, lastDayOfMonth } from "date-fns";
import Cookies from "js-cookie";
import { SeriesSearchParams } from "../interfaces/search-params.interface";

const customCookies = Cookies.withAttributes({ expires: 14 });

export type SortValue = "relevance" | "price_asc" | "price_desc";

export interface ListingFilterState {
  keyword: string;
  locationId: string | null;
  date: string | null;
  brands: string[];
  exEnd: string[];
  tags: string[];
  guarantees: string[];
  tier: string[];
  type: string | null;
  sort: SortValue;
}

export const EMPTY_FILTERS: ListingFilterState = {
  keyword: "",
  locationId: null,
  date: null,
  brands: [],
  exEnd: [],
  tags: [],
  guarantees: [],
  tier: [],
  type: null,
  sort: "relevance",
};

const splitList = (value: string | null): string[] =>
  value ? value.split(",").filter(Boolean) : [];

const isSortValue = (value: string | null): value is SortValue =>
  value === "relevance" || value === "price_asc" || value === "price_desc";

export const parseListingParams = (searchParams: URLSearchParams): ListingFilterState => {
  const sort = searchParams.get("sort");

  return {
    keyword: searchParams.get("keyword") || "",
    locationId: searchParams.get("location_id"),
    date: searchParams.get("date"),
    brands: splitList(searchParams.get("brands")),
    exEnd: splitList(searchParams.get("ex_end")),
    tags: splitList(searchParams.get("tags")),
    guarantees: splitList(searchParams.get("guarantees")),
    tier: splitList(searchParams.get("tier")),
    type: searchParams.get("type"),
    sort: isSortValue(sort) ? sort : "relevance",
  };
};

export const toQueryString = (state: ListingFilterState): string => {
  const params = new URLSearchParams();

  if (state.keyword) params.set("keyword", state.keyword);
  if (state.locationId) params.set("location_id", state.locationId);
  if (state.date) params.set("date", state.date);
  if (state.brands.length) params.set("brands", state.brands.join(","));
  if (state.exEnd.length) params.set("ex_end", state.exEnd.join(","));
  if (state.tags.length) params.set("tags", state.tags.join(","));
  if (state.guarantees.length) params.set("guarantees", state.guarantees.join(","));
  if (state.tier.length) params.set("tier", state.tier.join(","));
  if (state.type) params.set("type", state.type);
  if (state.sort !== "relevance") params.set("sort", state.sort);

  return params.toString();
};

const monthWindow = (date: string) => {
  const monthDate = new Date(date);
  return {
    year: monthDate.getFullYear().toString(),
    months: [format(monthDate, "yyyy-MM-01"), format(lastDayOfMonth(monthDate), "yyyy-MM-dd")],
  };
};

const agencyIdFromCookie = (): string | undefined => {
  const agency = customCookies.get("agency");
  if (!agency) return undefined;

  try {
    return JSON.parse(agency).id;
  } catch {
    return undefined;
  }
};

export const toSeriesSearchParams = (
  state: ListingFilterState,
  page = 1
): SeriesSearchParams => {
  const { year, months } = state.date ? monthWindow(state.date) : { year: undefined, months: null };

  return {
    keyword: state.keyword || undefined,
    year,
    months,
    location_id: state.locationId ? Number(state.locationId) : null,
    brands: state.brands.length ? state.brands : undefined,
    ex_end: state.exEnd.length ? state.exEnd : undefined,
    tags: state.tags.length ? state.tags : undefined,
    guarantees: state.guarantees.length ? state.guarantees : undefined,
    tier: state.tier.length ? state.tier : undefined,
    type: state.type || undefined,
    sortBy: state.sort === "price_desc" ? "highest_price" : undefined,
    page,
    agency_id: agencyIdFromCookie(),
  };
};

export const activeFilterCount = (state: ListingFilterState): number =>
  state.guarantees.length + state.tags.length + state.brands.length + state.tier.length;
